import type {
    ICompanyDomain,
    ICompanyDomainRefreshResult,
} from '@/api/organization/company-domain/get-company-domain-query'

interface ICompanyDomainReadback {
    name: string
    isConnected: boolean
    automaticClaimStatus: 'enabled' | 'disabled' | 'invalid'
}

interface ICompanyDomainClaimResult {
    outcome: 'success' | 'failure' | 'skipped'
    domain: string | null
}

export interface IConfirmedCompanyDomainClaim {
    organizationId: string
    domainName: string
}

/** Returns data only when the latest backend status request completed successfully. */
export function getSuccessfulCompanyDomainRefreshData(result: ICompanyDomainRefreshResult) {
    return result.isSuccess && !result.isError ? result.data : undefined
}

/** Requires an exact connected domain in a fresh backend Clerk readback. */
export function hasAuthoritativeCompanyDomainClaim(domains: ICompanyDomainReadback[], preparedDomainName: string) {
    const normalizedDomain = preparedDomainName.trim().toLowerCase()
    return domains.some(
        (domain) =>
            domain.name.trim().toLowerCase() === normalizedDomain &&
            domain.isConnected === true &&
            domain.automaticClaimStatus === 'enabled'
    )
}

/** Trusts success only because the backend returns it after its own exact Clerk readback. */
export function getBackendConfirmedCompanyDomainName(result: ICompanyDomainClaimResult) {
    if (result.outcome !== 'success' || !result.domain) return null
    return result.domain.trim().toLowerCase()
}

/** Keeps one backend-read-back-confirmed row visible while Clerk's following status read catches up. */
export function applyConfirmedCompanyDomainClaim(
    domains: ICompanyDomain['domains'],
    activeOrganizationId: string | undefined,
    confirmedClaim: IConfirmedCompanyDomainClaim | null
) {
    if (!activeOrganizationId || confirmedClaim?.organizationId !== activeOrganizationId) return domains

    const normalizedDomainName = confirmedClaim.domainName.trim().toLowerCase()
    const existingDomain = domains.find((domain) => domain.name.trim().toLowerCase() === normalizedDomainName)
    if (existingDomain?.isConnected && existingDomain.automaticClaimStatus === 'enabled') return domains

    const enrollmentMode =
        existingDomain?.enrollmentMode === 'automatic_invitation' ? 'automatic_invitation' : 'automatic_suggestion'
    const confirmedRow: ICompanyDomain['domains'][number] = {
        id: existingDomain?.id ?? `confirmed:${normalizedDomainName}`,
        name: normalizedDomainName,
        enrollmentMode,
        verificationStatus: 'verified',
        automaticClaimStatus: 'enabled',
        isClaimed: true,
        suggestionsEnabled: enrollmentMode === 'automatic_suggestion',
        isConnected: true,
        // The POST confirmed the domain name, not an actionable Clerk domain id. Actions remain
        // unavailable until the live GET returns the authoritative row.
        actions: {
            canTurnOffSuggestions: false,
            canTurnOnSuggestions: false,
            canRemove: false,
        },
    }

    return existingDomain
        ? domains.map((domain) => (domain.id === existingDomain.id ? confirmedRow : domain))
        : [...domains, confirmedRow]
}
